import type { LucideIcon } from "lucide-react";
import { ArrowLeft, Home } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
} from "@/components/ui/card";

type ErrorPageProps = {
  code: string;
  title: string;
  description: string;
  icon: LucideIcon;
  primaryActionLabel?: string;
  primaryActionPath?: string;
  showBackButton?: boolean;
};

export function ErrorPage({
  code,
  title,
  description,
  icon: Icon,
  primaryActionLabel = "Go to dashboard",
  primaryActionPath = "/",
  showBackButton = true,
}: ErrorPageProps) {
  const navigate = useNavigate();

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-950 px-4 py-12">
      <Card className="w-full max-w-lg border-slate-800 bg-slate-900 text-white shadow-2xl">
        <CardContent className="flex flex-col items-center px-8 py-12 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-500/10 text-emerald-400">
            <Icon className="h-8 w-8" />
          </div>

          <p className="mt-6 text-sm font-semibold uppercase tracking-[0.3em] text-emerald-400">
            Error {code}
          </p>

          <h1 className="mt-3 text-3xl font-semibold tracking-tight">
            {title}
          </h1>

          <p className="mt-4 max-w-md text-sm leading-6 text-slate-400">
            {description}
          </p>

          <div className="mt-8 flex w-full flex-col gap-3 sm:flex-row sm:justify-center">
            <Button
              type="button"
              onClick={() =>
                navigate(primaryActionPath, {
                  replace: true,
                })
              }
            >
              <Home className="mr-2 h-4 w-4" />
              {primaryActionLabel}
            </Button>

            {showBackButton && (
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate(-1)}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Go back
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
